import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { WorkflowFile } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useStageFormError } from '../hooks/use-stage-form-error';
import { useSendStage } from '../hooks/use-workflow-mutations';
import { isReviewMediaType, isTooLarge } from '../schemas/file-limits';
import { sendStageSchema, type SendStageForm } from '../schemas/stage-forms';
import { needsDelayReason } from '../schemas/stage-rules';

import { ActionDialog } from './action-dialog';
import { DelayReasonField } from './delay-reason-field';
import { MediaMultiPicker } from './media-multi-picker';
import type { StageTarget } from './stage-target';

const KNOWN = new Set(['mediaIds', 'message', 'certificateNumber', 'delayReason']);

export interface SendStageDialogProps {
  target: StageTarget;
  /** Files of the last QC submission, offered as the review media. */
  qcFiles?: WorkflowFile[];
  onOpenChange: (open: boolean) => void;
}

/**
 * send_files emails the picked files (an IGI certificate PDF) and completes the stage;
 * customer_review emails a signed link to the video and images and waits for the answer.
 */
export function SendStageDialog({ target, qcFiles = [], onOpenChange }: SendStageDialogProps) {
  const { t } = useTranslation('production');
  const review = target.type === 'customer_review';
  const send = useSendStage(
    target.storeId,
    target.orderId,
    review ? 'production:toasts.reviewSent' : 'production:toasts.filesSent',
  );
  const [forceDelay, setForceDelay] = useState(false);
  const showDelay =
    (target.run.status === 'in_progress' && needsDelayReason(target.run)) || forceDelay;
  const schema = useMemo(() => sendStageSchema(showDelay), [showDelay]);
  const form = useForm<SendStageForm>({
    resolver: useI18nResolver(schema),
    defaultValues: {
      mediaIds: [],
      message: '',
      certificateNumber: target.run.certificateNumber ?? '',
      delayReason: '',
    },
  });
  const onError = useStageFormError(form.setError, {
    knownFields: KNOWN,
    // `body.mediaIds[2]` points at one file; the picker shows the message as a whole.
    mapField: (field) => (field.startsWith('mediaIds.') ? 'mediaIds' : field),
    onDelayReasonRequired: () => setForceDelay(true),
  });
  const reviewable = qcFiles.filter((f) => isReviewMediaType(f.mimeType) && !isTooLarge(f.bytes));

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await send.mutateAsync({
        key: target.key,
        body: {
          mediaIds: values.mediaIds,
          message: values.message || null,
          ...(review ? {} : { certificateNumber: values.certificateNumber || null }),
          ...(showDelay && values.delayReason ? { delayReason: values.delayReason } : {}),
        },
      });
      onOpenChange(false);
    } catch (error) {
      onError(error);
    }
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        wide
        onOpenChange={onOpenChange}
        title={
          review
            ? t('send.reviewTitle', { name: target.name })
            : t('send.filesTitle', { name: target.name })
        }
        description={review ? t('send.reviewDescription') : t('send.filesDescription')}
        rootError={form.formState.errors.root?.server?.message}
        isPending={send.isPending}
        submitLabel={review ? t('send.submitReview') : t('send.submitFiles')}
        submittingLabel={t('send.submitting')}
        onSubmit={submit}
      >
        <FormField
          control={form.control}
          name="mediaIds"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('send.files')}</FormLabel>
              {review && reviewable.length > 0 ? (
                <Button
                  type="button"
                  variant="link"
                  className="self-start px-0"
                  onClick={() => field.onChange(reviewable.map((f) => f.mediaId))}
                >
                  {t('send.lastQc')}
                </Button>
              ) : null}
              <FormControl>
                <MediaMultiPicker
                  value={field.value}
                  onChange={field.onChange}
                  reviewMedia={review}
                  label={t('send.files')}
                  invalid={Boolean(fieldState.error)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {review ? null : (
          <FormField
            control={form.control}
            name="certificateNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('send.certificateNumber')}</FormLabel>
                <FormControl>
                  <Input {...field} maxLength={120} className="sm:max-w-xs" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('send.message')}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={3}
                  maxLength={2000}
                  placeholder={t('send.messagePlaceholder')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {showDelay ? <DelayReasonField run={target.run} /> : null}
      </ActionDialog>
    </Form>
  );
}
